import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import store from 'shared/redux/store'
import history from 'shared/utils/history'
import { unauthorize, logoutRequest, setCsrf } from 'shared/redux/auth/reducer'
import { showError, Notification } from 'shared/redux/app/reducer'

const baseURL = '/api'
const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
})

const noErrorToastMessageRoutes = [
  '/auth/login',
  '/membership-request',
  '/setting/organization',
  '/auth/forgot-password',
  '/check-in',
  '/public/invite/request',
]
const noErrorToastMessageRoutes400 = ['/export']

axiosInstance.interceptors.request.use(
  function(config: AxiosRequestConfig) {
    const csrfToken = store.getState().auth.csrf
    const newCsfrToken = config.headers['x-csrf-token']
    if (!newCsfrToken && csrfToken) {
      config.headers['x-csrf-token'] = csrfToken
    }
    config.headers['Authorization'] = `Basic ${Buffer.from(
      `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`,
    ).toString('base64')}`

    return config
  },
  (err: Error) => Promise.reject(err),
)

axiosInstance.interceptors.response.use(
  function(response) {
    return response.data
  },
  async function(error: AxiosError) {
    const { response } = error
    const originalConfig = error.config
    if (response?.status === 401) {
      if (originalConfig.url === '/setting/organization') {
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else if (originalConfig.url !== '/auth/session') {
        const notification: Notification = {
          message: 'Session has expired',
          subText: 'You have been logged out. Please log in again.',
          showSubText: true,
          type: 'error',
        }
        store.dispatch(showError(notification))
      }
      setTimeout(() => {
        store.dispatch(unauthorize())
      }, 2000)
    } else if (response?.data.code === 'EBADCSRFTOKEN') {
      if (originalConfig.url === '/auth/login' || originalConfig.url === '/membership-request') {
        const retry = originalConfig.headers.retry || 0
        if (!originalConfig.headers.retry) {
          originalConfig.headers.retry = retry + 1
          const responseCSRFData: { csrf: string } = await axiosInstance.get('/auth/csrf')
          originalConfig.headers['x-csrf-token'] = responseCSRFData.csrf
          store.dispatch(setCsrf(responseCSRFData))
          return axiosInstance(originalConfig)
        } else {
          window.location.reload()
        }
      } else {
        const notification: Notification = {
          message: 'Session has expired',
          subText: 'You have been logged out. Please log in again.',
          showSubText: true,
          type: 'error',
        }
        store.dispatch(showError(notification))

        if (originalConfig.url === '/auth/logout') {
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        } else {
          store.dispatch(logoutRequest())
        }
      }
    } else if (response?.status === 500) {
      const notification: Notification = { message: 'Something Went Wrong', type: 'error' }
      history.replace(`/404-page-not-found`)
      store.dispatch(showError(notification))
    } else if (
      response?.status === 400 &&
      ![...noErrorToastMessageRoutes, ...noErrorToastMessageRoutes400].some(e => response?.config.url?.includes(e))
    ) {
      const notification: Notification = { message: response?.data?.message, type: 'error' }
      if (notification.message === 'Token expired') {
        notification.showSubText = true
        notification.isSubTextHTML = true
        notification.subText = `The waiver link has expired, <a target="_blank" href="${process.env.REACT_APP_SUPPORT_LINK}">contact support</a> to resend the waiver.`
      }
      store.dispatch(showError(notification))
    } else if (response?.status === 403 && !noErrorToastMessageRoutes.some(e => response?.config.url?.includes(e))) {
      const notification: Notification = { message: response?.data?.message, type: 'error' }
      store.dispatch(showError(notification))
      history.push(`/`)
    } else if (response?.status === 404) {
      // history.replace({
      //   prefix: false,
      //   pathname: `/not-found`
      // })
    }
    // Do something with response error
    return Promise.reject(error)
  },
)

export default axiosInstance
